/**
 * Fetches the signed-in user's real @branchrouter.com alias (provisioning it on
 * demand) and renders EmailManagement with it, replacing the previous hardcoded
 * demo address. Inbound alerts addressed to this alias are routed to the user.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import EmailManagement from './EmailManagement';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { IncomingEmail } from '../../types/dashboard';

const EmailPageContainer: React.FC = () => {
  const { token } = useAuth();
  const [address, setAddress] = useState<string>('');
  const [createdAt, setCreatedAt] = useState<string>(new Date().toISOString());
  const [providerStatus, setProviderStatus] = useState<string | null>(null);
  const [providerNotice, setProviderNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [incomingEmails, setIncomingEmails] = useState<IncomingEmail[]>([]);
  const [loadingIncoming, setLoadingIncoming] = useState(false);

  const syncProvider = useCallback(async () => {
    setSyncing(true);
    setProviderNotice(null);
    try {
      const res = await apiService.syncMyEmailProvider(token || undefined);
      if (res.success && res.data) {
        setProviderStatus(res.data.provider_status);
        if (res.data.provider_status === 'active') {
          setProviderNotice(null);
        } else {
          setProviderNotice(res.data.message);
        }
      } else {
        setProviderNotice(res.error || 'Could not register this address on the mail server.');
      }
    } catch (e: unknown) {
      setProviderNotice(e instanceof Error ? e.message : 'Could not sync with mail provider.');
    } finally {
      setSyncing(false);
    }
  }, [token]);

  const loadIncomingEmails = useCallback(async () => {
    setLoadingIncoming(true);
    try {
      const res = await apiService.getMyIncomingEmails(token || undefined);
      if (res.success && res.data?.incoming_emails) {
        setIncomingEmails(
          res.data.incoming_emails.map((row) => ({
            id: row.id,
            from: row.from_address,
            subject: row.subject,
            body: row.body ?? '',
            receivedAt: row.received_at,
            processed: row.processed,
            executionStatus: row.execution_status ?? undefined,
            confidenceScore: row.confidence_score ?? undefined,
            alertId: row.alert_id ?? undefined,
            executionTime: row.execution_time_ms ?? undefined,
          })),
        );
      }
    } catch {
      // Non-fatal — alias provisioning still works without the inbox list.
    } finally {
      setLoadingIncoming(false);
    }
  }, [token]);

  const loadOrProvision = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let primary:
        | {
            email_address: string;
            created_at?: string;
            provider_status?: string | null;
          }
        | undefined;
      try {
        const existing = await apiService.getMyEmailAddresses(token || undefined);
        primary = existing.success
          ? existing.data?.email_addresses?.find((a) => a.is_active && a.is_primary)
          : undefined;
      } catch {
        primary = undefined;
      }
      if (primary) {
        setAddress(primary.email_address);
        if (primary.created_at) setCreatedAt(primary.created_at);
        const ps = (primary.provider_status || '').toLowerCase();
        setProviderStatus(primary.provider_status || null);
        if (ps && ps !== 'active') {
          setProviderNotice(
            'This address is saved in Branch Router but is not registered on Zoho Mail yet, so inbound mail will bounce. Click “Register on mail server” after your administrator configures Zoho.',
          );
        } else if (!ps) {
          // Unknown status (legacy DB) — attempt sync quietly.
          await syncProvider();
        }
        return;
      }
      const gen = await apiService.generateMyEmailAddress(token || undefined);
      if (gen.success && gen.data?.email_address) {
        setAddress(gen.data.email_address);
        setCreatedAt(new Date().toISOString());
        await syncProvider();
      } else {
        setError(gen.error || 'Could not provision your email address. Please try again.');
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not load your email address.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [token, syncProvider]);

  useEffect(() => {
    loadOrProvision();
  }, [loadOrProvision]);

  useEffect(() => {
    if (address) {
      loadIncomingEmails();
    }
  }, [address, loadIncomingEmails]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadOrProvision(), loadIncomingEmails()]);
  }, [loadOrProvision, loadIncomingEmails]);

  const handleRegenerate = useCallback(async () => {
    try {
      const res = await apiService.regenerateMyEmailAddress('User requested new address', token || undefined);
      if (res.success && res.data?.new_email) {
        setAddress(res.data.new_email);
        setCreatedAt(new Date().toISOString());
        await syncProvider();
      } else {
        await loadOrProvision();
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not regenerate your email address.');
    }
  }, [token, loadOrProvision, syncProvider]);

  if (loading && !address) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !address) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {providerNotice && (
        <Alert
          severity={providerStatus === 'failed' ? 'error' : 'warning'}
          sx={{ m: 2, mb: 0 }}
          action={
            <Button color="inherit" size="small" disabled={syncing} onClick={syncProvider}>
              {syncing ? 'Registering…' : 'Register on mail server'}
            </Button>
          }
        >
          {providerNotice}
        </Alert>
      )}
      <EmailManagement
        emailAddress={{
          address,
          isCompromised: false,
          createdAt,
        }}
        incomingEmails={incomingEmails}
        lowConfidenceAlerts={[]}
        onEmailCompromise={handleRegenerate}
        onRefreshEmails={refreshAll}
        onProcessLowConfidence={() => {}}
        storageUsage={0}
        storageLimit={1}
        loading={loading || loadingIncoming}
      />
    </Box>
  );
};

export default EmailPageContainer;
